// ════════════════════════════════════════════════════════════════════════════
// M2.7 — banco de preguntas PEMM-Empresa (cuestionario · 0749)
// Espejo del banco PEMM-Proceso (0745): 3 preguntas por capacidad × 13 = 39,
// escala 1-4. Cada pregunta es una AFIRMACIÓN DE MADUREZ derivada de los
// descriptores canónicos de Hammer (descriptores-pemm-empresa.ts): las 3 preguntas
// de una capacidad escalan E-2 → E-3 → E-4, de modo que la MEDIA de sus respuestas
// (1-4) rueda al nivel de la capacidad (ePorMedia en el scoring del backend) y el
// global = eslabón débil (MIN).
//
// clave = `${capacidadClave}_qN` → el backend deduce la capacidad parseando la clave.
// ⚠️ GOTCHA 0743: "Conciencia"/"Responsabilidad" se repiten entre categorías →
// las claves son únicas por prefijo (lid_conciencia vs gob_conciencia).
// ════════════════════════════════════════════════════════════════════════════
import type { CategoriaEmpresa } from './descriptores-pemm-empresa';

export interface PreguntaPemmEmpresa {
  /** `${capacidadClave}_qN` — única; el backend parte por el último `_q`. */
  clave: string;
  /** clave de la capacidad a la que agrega (CAPACIDADES_EMPRESA). */
  capacidad: string;
  categoria: CategoriaEmpresa;
  texto: string;
}

/** Escala 1-4 del cuestionario PEMM-Empresa (grado de cumplimiento de la afirmación). */
export interface OpcionEscalaEmpresa {
  valor: 1 | 2 | 3 | 4;
  etiqueta: string;
}
export const ESCALA_EMPRESA: OpcionEscalaEmpresa[] = [
  { valor: 1, etiqueta: 'Nada' },
  { valor: 2, etiqueta: 'Parcialmente' },
  { valor: 3, etiqueta: 'En gran medida' },
  { valor: 4, etiqueta: 'Plenamente' },
];

/** 39 preguntas (3 por capacidad). Orden = orden canónico de CAPACIDADES_EMPRESA. */
export const PREGUNTAS_PEMM_EMPRESA: PreguntaPemmEmpresa[] = [
  // ── LIDERAZGO ─────────────────────────────────────────────────────────────
  {
    clave: 'lid_conciencia_q1',
    capacidad: 'lid_conciencia',
    categoria: 'LIDERAZGO',
    texto:
      'Al menos un alto ejecutivo comprende a fondo el concepto de proceso, cómo mejora el rendimiento y lo que implica implantarlo.',
  },
  {
    clave: 'lid_conciencia_q2',
    capacidad: 'lid_conciencia',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo directivo ve la empresa en términos de procesos y ha desarrollado una visión de la empresa y de sus procesos.',
  },
  {
    clave: 'lid_conciencia_q3',
    capacidad: 'lid_conciencia',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo directivo ve su propio trabajo en términos de proceso y entiende la gestión por procesos como forma de gestionar el negocio.',
  },
  {
    clave: 'lid_alineacion_q1',
    capacidad: 'lid_alineacion',
    categoria: 'LIDERAZGO',
    texto:
      'Un alto ejecutivo ha asumido el liderazgo y la responsabilidad del programa de procesos.',
  },
  {
    clave: 'lid_alineacion_q2',
    capacidad: 'lid_alineacion',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo de Dirección está alineado con el programa de procesos y una red de personas lo promueve en toda la empresa.',
  },
  {
    clave: 'lid_alineacion_q3',
    capacidad: 'lid_alineacion',
    categoria: 'LIDERAZGO',
    texto:
      'Personas de toda la empresa muestran entusiasmo por la gestión por procesos y ejercen liderazgo en sus esfuerzos.',
  },
  {
    clave: 'lid_comportamiento_q1',
    capacidad: 'lid_comportamiento',
    categoria: 'LIDERAZGO',
    texto:
      'Un alto ejecutivo fija públicamente objetivos en términos de cliente y compromete recursos y cambios para lograrlos.',
  },
  {
    clave: 'lid_comportamiento_q2',
    capacidad: 'lid_comportamiento',
    categoria: 'LIDERAZGO',
    texto:
      'Los altos ejecutivos operan en equipo, gestionan la empresa por sus procesos y participan activamente en el programa.',
  },
  {
    clave: 'lid_comportamiento_q3',
    capacidad: 'lid_comportamiento',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo directivo realiza su propio trabajo como procesos, centrado en la planificación estratégica y en nuevas oportunidades.',
  },
  {
    clave: 'lid_estilo_q1',
    capacidad: 'lid_estilo',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo que lidera el programa es apasionado sobre la necesidad de cambio y ve los procesos como herramienta clave.',
  },
  {
    clave: 'lid_estilo_q2',
    capacidad: 'lid_estilo',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo directivo ha delegado control y autoridad en los propietarios y ejecutantes de proceso.',
  },
  {
    clave: 'lid_estilo_q3',
    capacidad: 'lid_estilo',
    categoria: 'LIDERAZGO',
    texto:
      'El equipo directivo lidera mediante visión e influencia, en lugar de órdenes y control.',
  },
  // ── CULTURA ───────────────────────────────────────────────────────────────
  {
    clave: 'cul_trabajo_equipo_q1',
    capacidad: 'cul_trabajo_equipo',
    categoria: 'CULTURA',
    texto:
      'La empresa usa equipos multidisciplinares con funciones cruzadas para los esfuerzos de mejora.',
  },
  {
    clave: 'cul_trabajo_equipo_q2',
    capacidad: 'cul_trabajo_equipo',
    categoria: 'CULTURA',
    texto:
      'El trabajo en equipo es la norma entre los ejecutantes y un hábito común entre los gerentes.',
  },
  {
    clave: 'cul_trabajo_equipo_q3',
    capacidad: 'cul_trabajo_equipo',
    categoria: 'CULTURA',
    texto: 'El trabajo en equipo con clientes y proveedores es un objetivo común.',
  },
  {
    clave: 'cul_enfoque_cliente_q1',
    capacidad: 'cul_enfoque_cliente',
    categoria: 'CULTURA',
    texto:
      'Los empleados entienden que el propósito de su trabajo es aportar un valor extraordinario al cliente.',
  },
  {
    clave: 'cul_enfoque_cliente_q2',
    capacidad: 'cul_enfoque_cliente',
    categoria: 'CULTURA',
    texto:
      'Los empleados entienden que los clientes exigen una excelencia uniforme y una experiencia transparente.',
  },
  {
    clave: 'cul_enfoque_cliente_q3',
    capacidad: 'cul_enfoque_cliente',
    categoria: 'CULTURA',
    texto:
      'Los empleados se enfocan en colaborar con socios (partners) para satisfacer al cliente final.',
  },
  {
    clave: 'cul_responsabilidad_q1',
    capacidad: 'cul_responsabilidad',
    categoria: 'CULTURA',
    texto: 'El personal de primera línea toma propiedad de los resultados, no solo los gerentes.',
  },
  {
    clave: 'cul_responsabilidad_q2',
    capacidad: 'cul_responsabilidad',
    categoria: 'CULTURA',
    texto: 'Los empleados se sienten responsables de los resultados de la empresa.',
  },
  {
    clave: 'cul_responsabilidad_q3',
    capacidad: 'cul_responsabilidad',
    categoria: 'CULTURA',
    texto:
      'Los empleados tienen un sentido de misión en el servicio al cliente y en lograr un rendimiento cada vez mejor.',
  },
  {
    clave: 'cul_actitud_cambio_q1',
    capacidad: 'cul_actitud_cambio',
    categoria: 'CULTURA',
    texto: 'Los empleados están preparados para un cambio significativo en la forma de trabajar.',
  },
  {
    clave: 'cul_actitud_cambio_q2',
    capacidad: 'cul_actitud_cambio',
    categoria: 'CULTURA',
    texto: 'Los empleados están preparados para cambios multidimensionales importantes.',
  },
  {
    clave: 'cul_actitud_cambio_q3',
    capacidad: 'cul_actitud_cambio',
    categoria: 'CULTURA',
    texto: 'Los empleados reconocen que el cambio es inevitable y lo abrazan como algo habitual.',
  },
  // ── CONOCIMIENTO ──────────────────────────────────────────────────────────
  {
    clave: 'con_personas_q1',
    capacidad: 'con_personas',
    categoria: 'CONOCIMIENTO',
    texto:
      'Existe un cuadro de expertos con habilidades en rediseño e implantación de procesos, gestión de proyectos, comunicación y gestión del cambio.',
  },
  {
    clave: 'con_personas_q2',
    capacidad: 'con_personas',
    categoria: 'CONOCIMIENTO',
    texto:
      'Existe un cuadro de expertos con habilidades en gestión del cambio y transformación de la empresa a gran escala.',
  },
  {
    clave: 'con_personas_q3',
    capacidad: 'con_personas',
    categoria: 'CONOCIMIENTO',
    texto:
      'Hay muchas personas con habilidades de rediseño y gestión del cambio en toda la empresa, y un proceso formal para desarrollarlas.',
  },
  {
    clave: 'con_metodologia_q1',
    capacidad: 'con_metodologia',
    categoria: 'CONOCIMIENTO',
    texto:
      'Los equipos de rediseño tienen acceso a una metodología básica de rediseño de procesos.',
  },
  {
    clave: 'con_metodologia_q2',
    capacidad: 'con_metodologia',
    categoria: 'CONOCIMIENTO',
    texto:
      'La empresa ha estandarizado un proceso formal de rediseño, integrado con un proceso de mejora continua.',
  },
  {
    clave: 'con_metodologia_q3',
    capacidad: 'con_metodologia',
    categoria: 'CONOCIMIENTO',
    texto:
      'La gestión y el rediseño de procesos son competencias básicas embebidas en un sistema formal (entorno, planificación, innovación).',
  },
  // ── GOBERNANZA ────────────────────────────────────────────────────────────
  {
    clave: 'gob_conciencia_q1',
    capacidad: 'gob_conciencia',
    categoria: 'GOBERNANZA',
    texto:
      'La empresa ha desarrollado un modelo empresarial orientado a procesos, aceptado por el equipo ejecutivo.',
  },
  {
    clave: 'gob_conciencia_q2',
    capacidad: 'gob_conciencia',
    categoria: 'GOBERNANZA',
    texto:
      'El modelo de procesos se ha comunicado en toda la empresa, impulsa la priorización y se alinea con la arquitectura de datos y tecnología.',
  },
  {
    clave: 'gob_conciencia_q3',
    capacidad: 'gob_conciencia',
    categoria: 'GOBERNANZA',
    texto:
      'La empresa ha ampliado su modelo de procesos para conectar con clientes y proveedores, y lo usa en la estrategia.',
  },
  {
    clave: 'gob_responsabilidad_q1',
    capacidad: 'gob_responsabilidad',
    categoria: 'GOBERNANZA',
    texto:
      'Los propietarios de proceso responden de sus procesos y un comité directivo responde del progreso global.',
  },
  {
    clave: 'gob_responsabilidad_q2',
    capacidad: 'gob_responsabilidad',
    categoria: 'GOBERNANZA',
    texto: 'Los propietarios de proceso comparten la responsabilidad del desempeño de la empresa.',
  },
  {
    clave: 'gob_responsabilidad_q3',
    capacidad: 'gob_responsabilidad',
    categoria: 'GOBERNANZA',
    texto:
      'Un consejo de procesos es el máximo órgano de gestión y existen comités con clientes y proveedores para el cambio interempresarial.',
  },
  {
    clave: 'gob_integracion_q1',
    capacidad: 'gob_integracion',
    categoria: 'GOBERNANZA',
    texto:
      'Un organismo coordina el programa de procesos y un comité directivo asigna recursos a los rediseños.',
  },
  {
    clave: 'gob_integracion_q2',
    capacidad: 'gob_integracion',
    categoria: 'GOBERNANZA',
    texto:
      'Una oficina formal de gestión de programas, dirigida por un director de procesos, coordina e integra todos los proyectos.',
  },
  {
    clave: 'gob_integracion_q3',
    capacidad: 'gob_integracion',
    categoria: 'GOBERNANZA',
    texto:
      'Los propietarios de proceso trabajan con sus contrapartes de clientes y proveedores para integrar procesos interempresariales.',
  },
];

/** Preguntas de una capacidad (en orden). */
export function preguntasDeCapacidad(capacidadClave: string): PreguntaPemmEmpresa[] {
  return PREGUNTAS_PEMM_EMPRESA.filter((p) => p.capacidad === capacidadClave);
}

/** Deriva la clave de la capacidad desde la clave de pregunta (`${cap}_qN`). */
export function capacidadDePregunta(preguntaClave: string): string {
  const i = preguntaClave.lastIndexOf('_q');
  return i > 0 ? preguntaClave.slice(0, i) : preguntaClave;
}

/** Forma persistida en `EvaluacionPEMMEmpresa.respuestas` (jsonb). */
export type RespuestasEmpresa = Record<string, { valor: number; nota?: string }>;
